import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import {FormAutofocusDirective} from '../../../shared/directives/form-autofocus.directive';

@Component({
  hostDirectives: [FormAutofocusDirective],
  standalone: false,
  selector: 'app-common-single-input-form',
  templateUrl: './common-single-input-form.component.html',
  styleUrls: ['./common-single-input-form.component.css', '../../../app.component.css']
})
export class CommonSingleInputFormComponent implements OnInit {

  /* ################### Variable Declaration Start ################### */
  @Input() MainModule!: string
  @Input() fieldData!: any;
  @Input() oldData?: any;

  commonForm!: FormGroup;
  submitted: boolean = false;
  closing: boolean = false;
  /* ################### Variable Declaration End ################### */

  constructor(
    public activeModel: NgbActiveModal,
    config: NgbModalConfig,
    public fb: FormBuilder
  ) {
  }

  /************************************
  * Life cycle hooks start
  ************************************/

  ngOnInit(): void {
    if (this.fieldData) {
      this.initForm()
    }
    if (this.oldData) {
      this.pathValueOfOld()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
  }

  /************************************
  * Life cycle hooks end
  ************************************/

  /************************************
  * Other functions start
  ************************************/

  get uf() {
    return this.commonForm.controls
  }

  pathValueOfOld() {
    this.commonForm.patchValue({
      field: this.oldData.value
    })
  }

  /**
   * True for a whole-number field. Kept as a getter so the template can switch the input's
   * inputmode and keypress guard without every existing caller having to declare a type.
   */
  get isNumber(): boolean {
    return this.fieldData?.type === 'number';
  }

  initForm() {
    const validation = this.fieldData?.validation ?? {};
    const validators = [];

    if (validation.required) {
      validators.push(Validators.required);
      // Whitespace-only is a text concern; on a numeric field the keypress guard already
      // makes it impossible and the pattern below would reject it anyway.
      if (!this.isNumber) {
        validators.push(this.noWhitespaceValidator);
      }
    }

    // Numeric constraints are opt-in, so text callers keep exactly the validators they had.
    if (this.isNumber) {
      validators.push(Validators.pattern(/^\d+$/));
      if (validation.min !== undefined && validation.min !== null) {
        validators.push(Validators.min(validation.min));
      }
      if (validation.max !== undefined && validation.max !== null) {
        validators.push(Validators.max(validation.max));
      }
    }

    this.commonForm = this.fb.group({
      field: new FormControl(null, validators)
    })
  }

  /**
   * Blocks anything but digits on a numeric field, so the control can never hold a decimal
   * point or a sign. No-op for text fields.
   */
  onKeyPress(event: any): boolean {
    if (!this.isNumber) return true;
    const charCode = event.which ? event.which : event.keyCode;
    return charCode >= 48 && charCode <= 57;
  }

  onSubmit() {
    this.submitted = true;
    if (this.commonForm.valid) {
      this.submitted = false;
      const raw = this.commonForm.value.field;
      let json = {
        ...this.commonForm.value,
        // An optional numeric field left blank means "not set" and must reach the caller as
        // null, not as the empty string a text input yields.
        field: this.isNumber && (raw === '' || raw === null || raw === undefined)
          ? null
          : (this.isNumber ? Number(raw) : raw),
      };
      this.commonForm.reset();
      this.activeModel.close(json);
    }
  }
  noWhitespaceValidator(control: any) {
    if (control.value == null || control.value.trim().length === 0) {
      if (control.value && control.value.trim().length === 0) {
        return { whitespace: true };  // Only spaces
      }
      return null; // empty handled by required validator
    }
    return null;
  }

  close() {
    // Suppress validation for good: as the modal fades out on close, the auto-focused
    // input loses focus and gets marked "touched", which would briefly flash the
    // required-field error. `closing` hides the message from that moment on.
    this.closing = true;
    this.submitted = false;
    this.commonForm?.reset();
    this.activeModel.close();
  }

  /************************************
  * Other functions end
  ************************************/

}
